import React, { useCallback, useMemo } from 'react';

import { Box, Flex, useTheme } from '@chakra-ui/react';
import InitiativeVisibility from 'components/RabbitInitiativeCreationPanel/PrimaryPanel/AdditionalInfoSection/InitiativeVisibility';
import InitiativeCreationDescription from 'components/RabbitInitiativeCreationPanel/PrimaryPanel/DetailsSection/InitiativeCreationDescription';
import { InitiativeName } from 'components/RabbitInitiativeCreationPanel/PrimaryPanel/DetailsSection/InitiativeName';
import { InputLabelHorizontal } from 'components/RabbitInitiativeCreationPanel/components/InputLabelHorizontal';
import { DetailsSectionHeader } from 'components/RabbitInitiativeCreationPanel/components/SectionHeaders';
import { InitiativeDueDate } from 'components/RabbitInitiativeSidePanel/PrimaryPanel/Sections/AdditionalInfo/InitiativeDueDate';
import { InitiativeStartDate } from 'components/RabbitInitiativeSidePanel/PrimaryPanel/Sections/AdditionalInfo/InitiativeStartDate';
import { OKROwnerAndCollaboratorComponent } from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/Details/OKROwnerAndCollaborator';
import { useDispatch, useSelector } from 'react-redux';
import { initiativeSecondaryPanelSelector } from 'selectors/initiativeSecondaryPanel';

import { SaveAndExit } from 'assets/assetsV1/iconComponents';

import { Accordion, AccordionButton, AccordionItem, AccordionPanel } from 'uiToolkitV1/Accordion';
import { DrawerFooter } from 'uiToolkitV1/BottomDrawer';
import { Button } from 'uiToolkitV1/Button';
import { Divider } from 'uiToolkitV1/Divider';
import { TextLabel } from 'uiToolkitV1/Typography';

import { CreateInitiativePanelHeader } from '../../Components/SectionHeaders';
import { onSubtaskChange } from '../../initiativeSecondaryPanelSlice';

export const CreateInitiativeSubtaskPanel = ({ onCollapse, handleSaveAndExit }) => {
  const { initiativeSubtaskDetails } = useSelector(initiativeSecondaryPanelSelector);
  const dispatch = useDispatch();

  const theme = useTheme();

  const containerStyles = {
    height: '100%',
    flex: 'auto 1 1',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '12px 8px',
    gap: '12px',

    background: '#FFFFFF',
    borderRadius: '8px',

    overflowY: 'auto',
  };

  const { description, text, toIds = [], visibilityType, startTS, dueTS, timeBoundDetails } = initiativeSubtaskDetails;

  const updatedToIds = useMemo(
    () =>
      toIds.map((data) => ({
        id: data?.uuid,
        displayName: data.displayName || data.metadata.displayName,
        email: data.email || data.metadata?.email,
        profilePhoto: data.profilePhoto || data?.profile?.profilePhoto,
        ownerType: data?.ownerType,
      })),
    [toIds]
  );

  const onChange = useCallback(
    (updates) => {
      dispatch(onSubtaskChange(updates));
    },
    [dispatch]
  );

  return (
    <Flex backgroundColor="#FFFFFF" h="100%" flexDirection="column">
      <CreateInitiativePanelHeader headerText="Add new initiative" onCollapse={onCollapse} />
      <Divider sx={{ borderColor: '#CFD7DF' }} />
      <Box {...containerStyles}>
        <Box w="100%">
          <DetailsSectionHeader />
          <Box>
            <InitiativeName text={text} onChange={(text) => onChange({ text })} />
          </Box>
        </Box>
        <Divider sx={{ borderColor: '#CFD7DF' }} />

        <Flex flexDirection="column" alignItems="stretch" justifyContent="center" gap="12px" flexWrap="nowrap" w="100%">
          <Accordion allowMultiple style={{ boxShadow: 'none' }}>
            <AccordionItem style={{ borderBottom: 'none' }}>
              <AccordionButton style={{ background: '#FFFFFF' }}>
                <TextLabel bold color="text.g1">
                  Add Additional Info
                </TextLabel>
              </AccordionButton>
              <AccordionPanel style={{ borderTop: 'none', padding: '0px 16px' }}>
                <InputLabelHorizontal labelText="Owners:">
                  <OKROwnerAndCollaboratorComponent
                    isOwner
                    ownersAndCollaborators={updatedToIds}
                    onChange={(toIds) => {
                      const updatedOwners = toIds.map((owners) => ({ ...owners, uuid: owners.id }));

                      onChange({ toIds: updatedOwners });
                    }}
                  />
                </InputLabelHorizontal>
                <InputLabelHorizontal labelText="Collaborators:">
                  <OKROwnerAndCollaboratorComponent
                    isOwner={false}
                    ownersAndCollaborators={updatedToIds}
                    onChange={(toIds) => {
                      const updatedOwners = toIds.map((owners) => ({ ...owners, uuid: owners.id }));

                      onChange({ toIds: updatedOwners });
                    }}
                  />
                </InputLabelHorizontal>

                <InputLabelHorizontal labelText="Visibility:">
                  <InitiativeVisibility
                    visibilityType={visibilityType}
                    onChange={(visibilityType) => onChange({ visibilityType })}
                  />
                </InputLabelHorizontal>
                <InitiativeCreationDescription
                  description={description}
                  onChange={(description) => onChange({ description })}
                />
                <InputLabelHorizontal labelText="Start Date:">
                  <InitiativeStartDate
                    startTS={startTS}
                    dueTS={dueTS}
                    isClearable
                    timeBoundDetails={timeBoundDetails}
                    onChange={(startTS) => onChange({ startTS })}
                  />
                </InputLabelHorizontal>
                <InputLabelHorizontal labelText="Due Date:">
                  <InitiativeDueDate
                    dueTS={dueTS}
                    startTS={startTS}
                    isClearable
                    timeBoundDetails={timeBoundDetails}
                    onChange={(dueTS) => onChange({ dueTS })}
                  />
                </InputLabelHorizontal>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </Flex>
      </Box>

      <DrawerFooter
        borderTopWidth="1px"
        h="72px"
        justifyContent="space-between"
        backgroundColor={theme.colors.neutral.F5F7F9}
      >
        <Button variant="tertiary" mr={4} onClick={onCollapse}>
          Cancel
        </Button>
        <Button variant="filled" StartIcon={SaveAndExit} onClick={handleSaveAndExit} disabled={false}>
          Save & Exit
        </Button>
        {/* <SaveAndAddComponent disabled={disableSubmit} /> */}
      </DrawerFooter>
    </Flex>
  );
};

export default CreateInitiativeSubtaskPanel;
